import { notify } from 'notiwind'

const REAUTH_CODES = ['OUTLOOK_REAUTH_REQUIRED', 'OUTLOOK_TOKEN_EXPIRED'] as const
const REAUTH_REASONS = ['token_expired', 'reauth_required', 'invalid_grant'] as const

export type OutlookDisconnectReason = (typeof REAUTH_REASONS)[number] | null

export type OutlookStatusResponse = {
  connected: boolean
  email?: string | null
  reason?: OutlookDisconnectReason
}

export type OutlookConnectResponse = {
  authUrl: string
}

type StrapiLikeError = {
  status?: number
  statusCode?: number
  message?: string
  code?: string
  reason?: string
  data?: {
    code?: string
    message?: string
    reason?: string
  }
}

export type OutlookReauthError = {
  error?: StrapiLikeError
} & StrapiLikeError

export type CheckStatusOptions = {
  silent?: boolean
}

export type HandleOutlookErrorOptions = {
  notifyUser?: boolean
}

export function isOutlookReauthError(error: unknown): boolean {
  const wrapped = error as OutlookReauthError
  const err = wrapped?.error || wrapped
  const status = err?.status ?? err?.statusCode
  const code = err?.data?.code ?? err?.code
  const reason = err?.data?.reason ?? err?.reason
  const message = (err?.message || err?.data?.message || '').toLowerCase()

  if (code && REAUTH_CODES.includes(code as (typeof REAUTH_CODES)[number])) return true
  if (reason && REAUTH_REASONS.includes(reason as (typeof REAUTH_REASONS)[number])) return true
  if (status === 401 && (message.includes('outlook') || message.includes('microsoft') || message.includes('graph'))) return true
  if (message.includes('invalid_grant') || message.includes('token expired') || message.includes('refresh token')) return true

  return false
}

export function useOutlook() {
  const client = useStrapiClient()
  const { fetchUser } = useUser()

  const isConnected = useState<boolean | null>('outlook-connected', () => null)
  const outlookEmail = useState<string | null>('outlook-email', () => null)
  const disconnectReason = useState<OutlookDisconnectReason>('outlook-disconnect-reason', () => null)
  const isCheckingStatus = useState('outlook-checking-status', () => false)
  const reauthNotified = useState('outlook-reauth-notified', () => false)

  const pollingRefCount = useState('outlook-polling-ref-count', () => 0)
  const statusIntervalId = useState<ReturnType<typeof setInterval> | null>('outlook-status-interval', () => null)

  const canUseOutlook = computed(() => isConnected.value === true)

  const needsOutlookReauth = computed(() => {
    const reason = disconnectReason.value
    return reason !== null && REAUTH_REASONS.includes(reason)
  })

  function markDisconnected(reason: OutlookDisconnectReason = null) {
    isConnected.value = false
    outlookEmail.value = null
    disconnectReason.value = reason
    fetchUser().catch(() => {})
  }

  async function checkStatus(options: CheckStatusOptions = {}): Promise<OutlookStatusResponse> {
    const { silent = false } = options

    if (!silent) isCheckingStatus.value = true

    try {
      const response = await client<OutlookStatusResponse>('outlook/status', { method: 'GET' })
      isConnected.value = response.connected
      outlookEmail.value = response.email ?? null

      if (!response.connected) {
        disconnectReason.value = response.reason ?? null
      } else {
        disconnectReason.value = null
        reauthNotified.value = false
      }

      return response
    } catch (error) {
      console.error('Error checking Outlook status:', error)
      if (isOutlookReauthError(error)) {
        markDisconnected('token_expired')
      }
      throw error
    } finally {
      if (!silent) isCheckingStatus.value = false
    }
  }

  function handleOutlookError(error: unknown, options: HandleOutlookErrorOptions = {}): boolean {
    const { notifyUser = true } = options
    if (!isOutlookReauthError(error)) return false

    markDisconnected('token_expired')

    if (notifyUser && !reauthNotified.value) {
      reauthNotified.value = true
      notify({
        group: 'main',
        type: 'warning',
        title: 'Connexion Outlook expirée',
        text: 'Veuillez reconnecter votre compte Outlook pour continuer à utiliser la messagerie et le calendrier.'
      })
    }

    return true
  }

  async function connect(redirectUri: string): Promise<OutlookConnectResponse> {
    disconnectReason.value = null
    const response = await client<OutlookConnectResponse>('outlook/connect', {
      method: 'GET',
      params: { redirectUri }
    })
    window.open(response.authUrl, '_blank')
    return response
  }

  async function disconnect(): Promise<void> {
    await client('outlook/disconnect', { method: 'POST' })
    markDisconnected(null)
    reauthNotified.value = false

    notify({
      group: 'main',
      type: 'success',
      title: 'Déconnexion réussie',
      text: 'Vous avez été déconnecté de votre compte Outlook.'
    })
  }

  function onWindowFocus() {
    checkStatus({ silent: true }).catch(() => {})
  }

  function startStatusPolling(intervalMs = 120000) {
    if (import.meta.server) return

    pollingRefCount.value++
    if (pollingRefCount.value > 1) return

    checkStatus({ silent: true }).catch(() => {})

    statusIntervalId.value = setInterval(() => {
      if (isConnected.value) {
        checkStatus({ silent: true }).catch(() => {})
      }
    }, intervalMs)

    window.addEventListener('focus', onWindowFocus)
  }

  function stopStatusPolling() {
    if (import.meta.server) return
    if (pollingRefCount.value <= 0) return

    pollingRefCount.value--
    if (pollingRefCount.value > 0) return

    if (statusIntervalId.value) {
      clearInterval(statusIntervalId.value)
      statusIntervalId.value = null
    }
    window.removeEventListener('focus', onWindowFocus)
  }

  return {
    isConnected,
    outlookEmail,
    disconnectReason,
    isCheckingStatus,
    canUseOutlook,
    needsOutlookReauth,
    checkStatus,
    handleOutlookError,
    connect,
    disconnect,
    startStatusPolling,
    stopStatusPolling
  }
}
